import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '资讯', to: '/category/news' },
  { label: '作品', to: '/category/works' },
  { label: '文化', to: '/category/culture' },
  { label: '专题', to: '/category/culture#topics' },
  { label: '关于', to: '/about' },
];

export default function SiteNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (to: string) => {
    const [path, hash] = to.split('#');
    if (hash) {
      return location.pathname === path && location.hash === `#${hash}`;
    }
    if (path === '/category/culture') {
      return location.pathname === path && location.hash !== '#topics';
    }
    return location.pathname === path;
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
    setKeyword('');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 border-background-200/80 backdrop-blur-md'
          : 'bg-background-50/80 border-transparent backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-semibold group-hover:bg-primary-600 transition-colors">
            璃
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-semibold tracking-[0.14em] text-foreground-950">
              琉璃神社
            </span>
            <span className="hidden sm:block mt-1 text-[10px] tracking-[0.28em] uppercase text-secondary-500 font-label">
              Ruri Shrine
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`relative px-3 py-2 text-sm transition-colors cursor-pointer ${
                isActive(item.to)
                  ? 'text-primary-700 font-medium'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {item.label}
              <span
                className={`absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-accent-500 transition-opacity ${
                  isActive(item.to) ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden md:flex items-center gap-2 h-10 w-[200px] lg:w-[268px] px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 focus-within:bg-background-50 transition-colors"
          >
            <i className="ri-search-line text-secondary-400 text-base w-4 h-4 flex items-center justify-center"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯、作品、文化"
              aria-label="站内搜索"
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-secondary-400 outline-none"
            />
            <button
              type="submit"
              className="text-[11px] text-secondary-500 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 rounded-lg border border-background-200 text-foreground-800 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden border-t border-background-200/70 bg-background-50 transition-all duration-300 ${
          menuOpen ? 'max-h-[460px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 h-11 w-full px-3 rounded-lg bg-background-100 border border-background-200 focus-within:border-primary-300"
          >
            <i className="ri-search-line text-secondary-400 text-base w-4 h-4 flex items-center justify-center"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索关键词"
              aria-label="站内搜索"
              className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-secondary-400"
            />
            <button
              type="submit"
              className="text-sm text-primary-600 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link
              to="/"
              className="px-3 py-3 rounded-lg bg-background-100 text-sm text-foreground-800 cursor-pointer"
            >
              首页
            </Link>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-3 rounded-lg text-sm cursor-pointer ${
                  isActive(item.to)
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'bg-background-100 text-foreground-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}